import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CONTRIBUTING_CONDITION_STATUS_OPTIONS,
  INPUT_NEEDED_FROM_OPTIONS,
  readWorkplaceCondition,
  type ContributingConditionStatus,
  type WorkplaceConditionFields,
} from '../../domain/workplaceCondition/workplaceCondition';
import { buildDiscouragedLanguageWarning } from '../../domain/reportLanguage/reportLanguageGuardrails';
import { reportsApi } from '../../services/reportsApi';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import SiteFlowReviewPromptsPanel from './SiteFlowReviewPromptsPanel';
import {
  type SafetyCaseAttestationSaveHandler,
  useRegisterSafetyCaseSaveHandler,
} from '../../domain/safetyCases/safetyCaseAttestationSave';

export type ContributingConditionPreview = {
  likelyContributingCondition: string;
  contributingConditionStatus: ContributingConditionStatus | '';
};

type ContributingConditionReviewSectionProps = {
  safetyCaseId: string;
  item: SafetyCaseRecord;
  readOnly?: boolean;
  onSaved?: (fields: WorkplaceConditionFields) => void;
  onPreviewChange?: (preview: ContributingConditionPreview) => void;
  hideSaveButton?: boolean;
  saveHandlerRef?: React.MutableRefObject<SafetyCaseAttestationSaveHandler | null>;
};

function fieldsFromItem(item: SafetyCaseRecord): WorkplaceConditionFields {
  return readWorkplaceCondition(item.scope);
}

export const ContributingConditionReviewSection: React.FC<ContributingConditionReviewSectionProps> = ({
  safetyCaseId,
  item,
  readOnly = false,
  onSaved,
  onPreviewChange,
  hideSaveButton = false,
  saveHandlerRef,
}) => {
  const [likelyContributingCondition, setLikelyContributingCondition] = useState('');
  const [contributingConditionStatus, setContributingConditionStatus] = useState<ContributingConditionStatus | ''>('');
  const [inputNeededFrom, setInputNeededFrom] = useState('');
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const conditionTypeId = useMemo(
    () => String(fieldsFromItem(item).condition_type || '').trim(),
    [item],
  );

  useEffect(() => {
    const fields = fieldsFromItem(item);
    setLikelyContributingCondition(String(fields.likely_contributing_condition || '').trim());
    setContributingConditionStatus(
      (String(fields.contributing_condition_status || '').trim() as ContributingConditionStatus) || '',
    );
    setInputNeededFrom(String(fields.input_needed_from || '').trim());
    setReviewerNotes(String(fields.reviewer_notes || '').trim());
    setSaveError(null);
    setSaveNotice(null);
  }, [item]);

  useEffect(() => {
    onPreviewChange?.({
      likelyContributingCondition,
      contributingConditionStatus,
    });
  }, [contributingConditionStatus, likelyContributingCondition, onPreviewChange]);

  const likelyContributingWarning = useMemo(
    () => buildDiscouragedLanguageWarning(likelyContributingCondition),
    [likelyContributingCondition],
  );
  const reviewerNotesWarning = useMemo(
    () => buildDiscouragedLanguageWarning(reviewerNotes),
    [reviewerNotes],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    if (!hideSaveButton) setSaveNotice(null);
    try {
      const response = await reportsApi.patchWorkplaceCondition(safetyCaseId, {
        likely_contributing_condition: likelyContributingCondition.trim() || null,
        contributing_condition_status: contributingConditionStatus || null,
        input_needed_from: inputNeededFrom.trim() || null,
        reviewer_notes: reviewerNotes.trim() || null,
      });
      const saved = readWorkplaceCondition(response.safety_case?.scope || response.finding?.scope);
      onSaved?.(saved);
      if (!hideSaveButton) setSaveNotice('Contributing condition review saved.');
    } catch (error: any) {
      setSaveError(error?.message || 'Unable to save contributing condition review right now.');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [
    contributingConditionStatus,
    hideSaveButton,
    inputNeededFrom,
    likelyContributingCondition,
    onSaved,
    reviewerNotes,
    safetyCaseId,
  ]);

  useRegisterSafetyCaseSaveHandler(readOnly ? undefined : saveHandlerRef, handleSave);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }} data-testid="contributing-condition-review-section">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
          Contributing Condition Review
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Document the reviewer hypothesis or confirmation status. This section supports EHS review and does not represent automated root-cause diagnosis.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Likely contributing condition"
            value={likelyContributingCondition}
            onChange={(event) => setLikelyContributingCondition(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            helperText="Optional reviewer hypothesis. Do not state root cause unless confirmed later."
          />
          {likelyContributingWarning ? (
            <Alert severity="warning" data-testid="likely-contributing-language-warning">
              {likelyContributingWarning}
            </Alert>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="contributing-condition-status-label">Contributing condition status</InputLabel>
            <Select
              labelId="contributing-condition-status-label"
              label="Contributing condition status"
              value={contributingConditionStatus}
              onChange={(event) => setContributingConditionStatus(
                String(event.target.value || '') as ContributingConditionStatus | '',
              )}
            >
              <MenuItem value="">
                <em>Select status</em>
              </MenuItem>
              {CONTRIBUTING_CONDITION_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="input-needed-from-label">Input needed from</InputLabel>
            <Select
              labelId="input-needed-from-label"
              label="Input needed from"
              value={inputNeededFrom}
              onChange={(event) => setInputNeededFrom(String(event.target.value || ''))}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {INPUT_NEEDED_FROM_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Reviewer notes"
            value={reviewerNotes}
            onChange={(event) => setReviewerNotes(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            helperText="Optional notes supporting the review decision."
          />
          {reviewerNotesWarning ? (
            <Alert severity="warning" data-testid="reviewer-notes-language-warning">
              {reviewerNotesWarning}
            </Alert>
          ) : null}
          {conditionTypeId ? (
            <SiteFlowReviewPromptsPanel
              conditionTypeId={conditionTypeId}
              title="Review prompts from site-flow content pack"
            />
          ) : null}
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saveNotice ? <Alert severity="success">{saveNotice}</Alert> : null}
          {!readOnly && !hideSaveButton ? (
          <Box>
            <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save contributing condition review'}
            </Button>
          </Box>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ContributingConditionReviewSection;
